import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Printer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { getReservations } from '@/lib/booking/api';
import { fetchCareBoard } from '@/lib/disruptions/api';
import { fetchCaseTrips } from '@/lib/transport/api';
import { caseTripsKey, splitTripsForGroup } from '@/lib/transport/trips';
import { TRIP_LEG_LABEL_KEY, isTerminalTripStatus, type TripView } from '@/lib/transport/types';
import { formatFlightDateTime } from '@/lib/disruptions/airport-tz';
import type { Reservation } from '@/lib/booking/types';
import type { CareCardRow, DisruptionPnrGroupRow } from '@/lib/disruptions/types';
import { PnrCouponSheet } from './pnr-coupon-sheet';
import { useExerciseMode } from '@/lib/system-status/use-exercise-mode';
import {
  DEAD_RESERVATION,
  SHEET_TITLE_KEY,
  buildNextSteps,
  careMoney,
  fmtDateTime,
  passengerListLabel,
  tripWhen,
  type PnrPrintKind,
} from './pnr-sheet-model';
import { usePrintPassengers } from './use-print-passengers';
import {
  A4_FORMAT,
  PRINT_FORMATS,
  applyPrintPageRule,
  getPrintFormat,
  isCouponStock,
  readPrintFormat,
  writePrintFormat,
  type PrintFormatId,
  type PrintFormatSpec,
} from './print-formats';

// Printable PNR sheets (drawer utility actions).
//
// IN THIS TAB, ON PURPOSE. This used to be a route opened with
// `window.open('/print/…', '_blank')`. That is unfixable under this app's Auth0
// setup: the provider runs `cacheLocation="memory"` with
// `useRefreshTokensFallback={false}` (auth-provider.tsx), so the token cache and
// the refresh token live in ONE tab's JS memory. A new tab starts with an empty
// cache, cannot re-authenticate silently, and is bounced to the IdP — the
// operator clicks "voucher" and gets a login screen instead of paper, which is
// exactly what happened in production. The route was also mounted OUTSIDE
// `AppShell`, so it never even had `withAuthenticationRequired` to lean on: its
// reads just 401'd into `forceLogout`.
//
// So the sheet is a DIALOG in the tab that already holds the session. No popup
// to be blocked, no second login, no navigation away from the drawer the
// operator is working — and the reads it needs (reservations, care board,
// trips) are the SAME query keys the drawer already filled, so the preview is
// usually instant. Printing is CSS: `.print-sheet-open` in index.css hides the
// console and unwraps the dialog so `[data-print-sheet]` is the whole page.
//
// FOUR KINDS, ONE OF THEM THE WHOLE STORY. The three single sheets (flight
// voucher, hotel voucher, how-to-get-there) each answer one question. But a
// passenger who is being re-accommodated overnight is handed all of them at
// once, plus a card they have to be told about and a car that may or may not be
// booked yet — four pieces of paper for one night, none of which says what to do
// after the one thing it covers. `travel-pack` is that night on ONE sheet:
// flight, hotel, the money on the card, the ground transport, the directions,
// and a numbered "what happens next" built from the facts that actually exist.
// The single sheets stay — reprinting just the hotel voucher for a front desk is
// still a real need — but the pack is the default hand-out when there is more
// than one thing to hand out.
//
// …AND TWO KINDS OF PAPER, WHICH IS A DIFFERENT AXIS. What the sheet SAYS (kind)
// and what it is printed ON (format) are independent, and the console was only
// ever right about the first one: it assumed an A4 laser printer, while the
// printer standing next to the agent at a check-in desk is the one that prints
// air tickets — an ATB unit on 8 × 3¼ in coupon stock, or a thermal roll. Those
// two need their own geometry AND their own layout (see `pnr-coupon-sheet.tsx`),
// and nothing in the app can detect which one is loaded. So the operator is
// asked, every time the dialog opens, with their last answer preselected: a
// visible choice, because A4 markup fired at a stack of ticket stock is a jam
// and a reload at 2 a.m.
//
// NOTHING PRINTS BY ITSELF ANY MORE. The sheet used to fire `window.print()` on
// its own once the reads settled. That was fine while paper meant "a sheet out
// of the tray" and wrong the moment it can mean ticket stock: auto-printing
// spends a consumable before the operator has said what is loaded. The Print
// button is focused instead, so the fast path is still one keypress — and it
// stays disabled until every read this sheet asked for has SETTLED, which is the
// guarantee the old auto-print gate was really there for.
//
// DEGRADE, NEVER BLOCK. The pack reads three sources beyond the roster row it is
// handed. All of them may be unavailable — the wallet may not answer, the
// transport domain may be down, a standalone case has no parent disruption and
// therefore no care board at all. A section whose source did not answer says so
// in the passenger's words ("ask an agent"); it never prints a fabricated
// $0.00, an empty box, or a promise nobody made.
//
// PII: the voucher names the passengers IN FULL, and that is the point of it —
// a hotel front desk and an airline counter check the paper against a passport,
// and "J*** P***" cannot be checked against anything. This is not an exemption
// from mask-by-default (TDS §14): the names come down the same audited reveal
// every other unmasking in this console uses, so printing a voucher leaves a
// PASSENGER_PII_REVEAL record naming the operator, the case and the passengers.
// See `use-print-passengers.ts`.

export type { PnrPrintKind };

const TENANT = (import.meta.env.VITE_TENANT ?? 'nexa').toUpperCase();

/**
 * The sheet, as a dialog over the drawer that opened it.
 *
 * `kind === null` is closed — the caller keeps one piece of state for "which
 * sheet, if any", so opening a second one is impossible and the queries below
 * stay disabled while nothing is being printed.
 *
 * The dialog is `z-[70]`: the PNR operation center it opens from is itself a
 * Radix dialog at `z-50`, and a nested dialog has to sit above it (the same
 * reason `UpdateContactDialog` runs at `z-[60]`; the sheet stacks above both).
 */
export function PnrPrintDialog({
  group,
  kind,
  onClose,
}: {
  group: DisruptionPnrGroupRow;
  kind: PnrPrintKind | null;
  onClose: () => void;
}) {
  const { t } = useTranslation('pnrActions');
  const open = kind !== null;
  const [formatId, setFormatId] = useState<PrintFormatId>(() => readPrintFormat());
  const format = getPrintFormat(formatId);
  /** Every read this sheet asked for has settled — see the header note. */
  const [ready, setReady] = useState(false);

  // The console has to be hidden from the printer for as long as this dialog is
  // mounted — not just while `window.print()` runs — so an operator who reaches
  // for Ctrl+P instead of the button gets the sheet too, not a screenshot of the
  // console. The class is the ONLY switch for the print rules in index.css:
  // without it a plain Ctrl+P anywhere else in the app is untouched.
  useEffect(() => {
    if (!open) return;
    document.body.classList.add('print-sheet-open');
    return () => document.body.classList.remove('print-sheet-open');
  }, [open]);

  // Geometry follows the chosen stock and is torn down with the dialog, so the
  // rest of the console keeps its own page setup.
  useEffect(() => {
    if (!open) return;
    return applyPrintPageRule(format);
  }, [open, format]);

  // A different sheet is a different set of reads: don't inherit the last one's
  // "everything settled".
  useEffect(() => setReady(false), [kind]);

  // The fast path stays one keypress: as soon as the reads settle, focus lands
  // on Print so Enter sends it. Done with a ref rather than `autoFocus` because
  // the prop is banned here (jsx-a11y) — and because focusing on READY is the
  // behaviour we actually want, not focusing on mount while the sheet is still
  // filling in.
  const printRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (open && ready) printRef.current?.focus();
  }, [open, ready]);

  const chooseFormat = (id: PrintFormatId): void => {
    setFormatId(id);
    writePrintFormat(id);
  };

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? undefined : onClose())}>
      <DialogContent
        className="z-[70] flex h-[88dvh] w-[92vw] max-w-3xl flex-col gap-0 overflow-hidden p-0"
        data-print-shell
      >
        <DialogHeader className="border-b border-border px-5 py-3" data-print-chrome>
          <DialogTitle className="flex items-center gap-2">
            <Printer className="h-4 w-4" aria-hidden />
            {t('print.dialogTitle', {
              title: kind ? t(SHEET_TITLE_KEY[kind]) : '',
              pnr: group.pnrLabel,
            })}
          </DialogTitle>
          <DialogDescription>{t('print.dialogDescription')}</DialogDescription>
        </DialogHeader>

        <PrintFormatPicker selected={formatId} onSelect={chooseFormat} />

        <div className="flex-1 overflow-y-auto bg-neutral-100 p-6" data-print-scroll>
          {kind ? (
            <PnrPrintSheet
              key={kind}
              group={group}
              kind={kind}
              format={format}
              onReady={setReady}
            />
          ) : null}
        </div>

        <div
          className="flex items-center justify-end gap-2 border-t border-border px-5 py-3"
          data-print-chrome
        >
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="print-close">
            {t('print.close')}
          </Button>
          <Button
            ref={printRef}
            size="sm"
            disabled={!ready}
            onClick={() => window.print()}
            data-testid="print-again"
          >
            <Printer className="mr-1 h-3.5 w-3.5" aria-hidden />
            {ready
              ? t('print.print', { format: t(format.labelKey) })
              : t('print.preparing')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The one question the console cannot answer for itself.
 *
 * There is no API that reports what stock is in the printer, and no browser API
 * that lets a page choose a destination — the OS dialog owns that, deliberately.
 * What this can do is make the geometry right and say, in one line, the two
 * settings over there that ruin ticket stock: "Fit to page" scaling and default
 * margins. Both default to wrong for a coupon.
 */
function PrintFormatPicker({
  selected,
  onSelect,
}: {
  selected: PrintFormatId;
  onSelect: (id: PrintFormatId) => void;
}) {
  const { t } = useTranslation('pnrActions');
  const format = getPrintFormat(selected);
  return (
    <div
      className="flex flex-wrap items-center gap-2 border-b border-border bg-muted/40 px-5 py-2.5"
      data-print-chrome
      data-testid="print-format-picker"
    >
      <span className="text-xs font-medium text-muted-foreground">{t('print.paperLabel')}</span>
      {PRINT_FORMATS.map((f) => (
        <Button
          key={f.id}
          size="sm"
          variant={f.id === selected ? 'default' : 'outline'}
          aria-pressed={f.id === selected}
          title={`${t(f.sizeLabelKey)} — ${t(f.hintKey)}`}
          onClick={() => onSelect(f.id)}
          data-testid={`print-format-${f.id}`}
        >
          {t(f.labelKey)}
        </Button>
      ))}
      <p className="basis-full pt-1 text-[11px] leading-snug text-muted-foreground">
        <span className="font-medium">{t(format.sizeLabelKey)}</span> · {t(format.driverHintKey)}
      </p>
    </div>
  );
}

/**
 * The paper itself.
 *
 * Takes the roster row it is printing rather than re-reading it: the drawer that
 * opened this dialog already holds the row, so there is no PNR lookup here to be
 * slow, to be stale, or to fail. The three reads that remain are keyed exactly
 * like the app's own (`['cases', urn, 'reservations']`, `useCareBoard`,
 * `useCaseTrips`), so they are normally already warm in this tab — and each is
 * `retry: false`, because a sheet has to reach paper fast or say what it could
 * not read, not sit on a retry ladder while an operator waits with a passenger
 * at the counter.
 *
 * `format` decides which renderer draws it: a document you read in two hands, or
 * coupons off a ticket printer. Both take their facts from `pnr-sheet-model.ts`,
 * so the two can never disagree about the same night.
 */
export function PnrPrintSheet({
  group,
  kind,
  format = A4_FORMAT,
  onReady,
}: {
  group: DisruptionPnrGroupRow;
  kind: PnrPrintKind;
  format?: PrintFormatSpec;
  onReady?: (ready: boolean) => void;
}) {
  const { t } = useTranslation('pnrActions');
  const isPack = kind === 'travel-pack';
  const caseUrn = group.caseUrn;
  const subCaseUrn = group.subCaseUrn;
  // #812: a rehearsal box stamps every printed piece. Off (or BFF unreachable) = no stamp.
  const exerciseLabel = useExerciseMode()?.label;

  // The names on the paper. Audited, settled before the Print button unlocks —
  // a voucher that reaches a front desk still masked is a voucher that gets
  // handed back.
  const { passengers: printPassengers, isFetched: namesFetched } = usePrintPassengers(group, true);
  const passengerNames = useMemo(
    () => printPassengers.map((p) => p.name),
    [printPassengers],
  );

  const wantsHotel = kind === 'hotel-voucher' || kind === 'hotel-directions' || isPack;
  const reservationsQuery = useQuery({
    queryKey: ['cases', caseUrn, 'reservations'],
    queryFn: () => getReservations(caseUrn),
    enabled: !!caseUrn && wantsHotel,
  });
  const reservation: Reservation | undefined = reservationsQuery.data?.find(
    (r) => r.subCaseUrn === subCaseUrn && !DEAD_RESERVATION.includes(r.status),
  );

  // The care board is disruption-scoped: a standalone case (no parent
  // disruption) has none, and the entitlement denorm on the roster row is then
  // the only thing the card section has to go on.
  const careUrn = isPack ? group.disruptionUrn : undefined;
  const careQuery = useQuery({
    queryKey: ['disruptions', careUrn, 'care'],
    queryFn: () => fetchCareBoard(careUrn as string),
    enabled: !!careUrn,
    staleTime: 30_000,
    retry: false,
  });
  const card: CareCardRow | undefined = careQuery.data?.cards.find(
    (c) => c.subCaseUrn === subCaseUrn,
  );
  /** `known.wallet === false` = balances were NOT read — never print $0.00 then. */
  const balancesKnown = careQuery.data?.known.wallet !== false;

  const tripsQuery = useQuery({
    queryKey: caseTripsKey(caseUrn),
    queryFn: () => fetchCaseTrips(caseUrn),
    enabled: isPack && !!caseUrn,
    staleTime: 30_000,
    retry: false,
  });

  // Rides that are still going to happen. A completed or cancelled trip is
  // history, and history is not an instruction — the passenger's sheet lists
  // only what they still have to show up for, earliest first.
  const upcomingTrips = useMemo<TripView[]>(() => {
    const { mine } = splitTripsForGroup(tripsQuery.data, {
      subCaseUrn: group.subCaseUrn,
      pnrLabel: group.pnrLabel,
      pnrUrn: group.pnrUrn,
      paxUrns: group.passengers?.map((p) => p.paxUrn) ?? [],
    });
    return mine
      .filter((t) => !isTerminalTripStatus(t.status))
      .sort((a, b) =>
        (a.scheduledAt ?? a.requestedAt).localeCompare(b.scheduledAt ?? b.requestedAt),
      );
  }, [group, tripsQuery.data]);

  // "Every read this sheet asked for has SETTLED (resolved or failed)". Printing
  // before that would put "ask an agent about your card" on paper for a card
  // that was about to arrive one tick later — so the button upstairs stays
  // disabled until this flips.
  const ready =
    namesFetched &&
    (!wantsHotel || reservationsQuery.isFetched) &&
    (!careUrn || careQuery.isFetched) &&
    (!isPack || tripsQuery.isFetched);
  useEffect(() => {
    onReady?.(ready);
  }, [ready, onReady]);

  const mapsHref = useMemo(() => {
    if (!reservation?.hotelName) return undefined;
    const q = encodeURIComponent(`${reservation.hotelName} near ${group.destination ?? ''} airport`);
    return `https://www.google.com/maps/search/?api=1&query=${q}`;
  }, [reservation?.hotelName, group.destination]);

  // The two hotel-only sheets are meaningless without a booking; the pack is not
  // — it prints whatever the passenger actually has.
  if (wantsHotel && !isPack && !reservation) {
    return (
      <p className="mx-auto max-w-2xl bg-white p-10 font-mono text-sm text-black">
        {reservationsQuery.isFetched ? t('sheet.noHotelReservation') : t('sheet.preparing')}
      </p>
    );
  }

  const hasFlight = !!group.assignedFlight || !!group.ticketNumber;
  const entitlement = group.mealVoucher;
  const hasCareMoney = !!card || typeof entitlement?.amountUsd === 'number';

  // Ticket stock and the receipt roll are a different document, not a smaller
  // one: coupons that get separated, each carrying its own header and its own
  // instruction. Same facts, drawn for the paper they are going to.
  if (isCouponStock(format)) {
    return (
      <PnrCouponSheet
        format={format}
        exerciseLabel={exerciseLabel}
        source={{
          group,
          kind,
          reservation,
          card,
          balancesKnown,
          careUnavailable: careQuery.isError || (!careUrn && !card),
          trips: upcomingTrips,
          transportUnavailable: tripsQuery.isError,
          mapsHref,
          passengerNames,
          tenant: TENANT,
        }}
      />
    );
  }

  return (
    <article
      className="mx-auto max-w-2xl bg-white p-10 text-black shadow-sm print:p-0 print:shadow-none"
      data-print-sheet
      data-print-format={format.id}
    >
      {/* #812: the document sheet gets the same band as the coupons, above the header. */}
      {exerciseLabel ? (
        <p
          role="note"
          data-testid="sheet-exercise-band"
          className="mb-3 border-2 border-dashed border-black px-3 py-1.5 text-center text-sm font-bold uppercase tracking-[0.2em]"
        >
          {exerciseLabel}
        </p>
      ) : null}
      <header className="border-b-2 border-black pb-3">
        <p className="text-xs uppercase tracking-[0.3em] text-neutral-500">
          {t('sheet.tenantLine', { tenant: TENANT })}
        </p>
        <h1 className="mt-1 text-2xl font-bold">{t(SHEET_TITLE_KEY[kind])}</h1>
        {isPack ? (
          <p className="mt-1 text-sm text-neutral-600">{t('sheet.packIntro')}</p>
        ) : null}
      </header>

      <section className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
        <Field label={t('sheet.pnr')} value={group.pnrLabel} mono />
        <Field
          label={t('sheet.passengers', { count: group.paxCount })}
          value={t('sheet.paxValue', {
            names: passengerListLabel(passengerNames, 8, t),
            pax: group.paxCount,
          })}
        />
      </section>

      {isPack ? (
        <NextStepsSection
          group={group}
          reservation={reservation}
          card={card}
          hasCareMoney={hasCareMoney}
          trips={upcomingTrips}
        />
      ) : null}

      {kind === 'flight-voucher' || (isPack && hasFlight) ? <FlightSection group={group} /> : null}

      {(kind === 'hotel-voucher' || isPack) && reservation ? (
        <HotelSection group={group} reservation={reservation} />
      ) : null}

      {isPack && hasCareMoney ? (
        <CareCardSection
          card={card}
          entitlement={entitlement}
          balancesKnown={balancesKnown}
          careUnavailable={careQuery.isError || (!careUrn && !card)}
        />
      ) : null}

      {isPack ? (
        <TransportSection
          group={group}
          trips={upcomingTrips}
          hasHotel={!!reservation}
          unavailable={tripsQuery.isError}
        />
      ) : null}

      {(kind === 'hotel-directions' || isPack) && reservation ? (
        <DirectionsSection group={group} reservation={reservation} mapsHref={mapsHref} />
      ) : null}

      {isPack && !hasFlight && !reservation && !hasCareMoney ? (
        <section className="mt-6 text-sm" data-testid="sheet-empty">
          <p className="rounded-md border border-neutral-300 p-4">{t('sheet.empty')}</p>
        </section>
      ) : null}

      <footer className="mt-8 border-t border-neutral-300 pt-3 text-[11px] text-neutral-500">
        {t('sheet.footer', {
          issued: new Date().toLocaleString(),
          case: caseUrn.split(':').pop() ?? '',
          tenant: TENANT,
        })}
      </footer>
    </article>
  );
}

/** The numbered "what happens next" — see `buildNextSteps` for the rules. */
function NextStepsSection({
  group,
  reservation,
  card,
  hasCareMoney,
  trips,
}: {
  group: DisruptionPnrGroupRow;
  reservation: Reservation | undefined;
  card: CareCardRow | undefined;
  hasCareMoney: boolean;
  trips: readonly TripView[];
}) {
  const { t } = useTranslation('pnrActions');
  const steps = buildNextSteps({ group, reservation, card, hasCareMoney, trips }, t);
  if (!steps.length) return null;

  return (
    <section className="mt-6 break-inside-avoid space-y-2 text-sm" data-testid="sheet-next-steps">
      <h2 className="text-xs font-semibold uppercase tracking-wide text-neutral-500">
        {t('sheet.nextSteps')}
      </h2>
      <ol className="list-decimal space-y-1.5 rounded-md border border-neutral-300 p-4 pl-9">
        {steps.map((s) => (
          <li key={s}>{s}</li>
        ))}
      </ol>
    </section>
  );
}

function FlightSection({ group }: { group: DisruptionPnrGroupRow }) {
  const { t } = useTranslation('pnrActions');
  return (
    <section className="mt-6 break-inside-avoid space-y-2 text-sm" data-testid="sheet-flight">
      <h2 className="text-xs font-semibold uppercase tracking-wide text-neutral-500">
        {t('sheet.flight.title')}
      </h2>
      <div className="rounded-md border border-neutral-300 p-4">
        <p className="text-xl font-bold">
          {group.assignedFlight?.number ?? group.flightNumber}{' '}
          <span className="font-normal">
            {group.assignedFlight?.origin ?? group.origin} → {group.assignedFlight?.destination ?? group.destination}
          </span>
        </p>
        <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2">
          <Field
            label={t('sheet.flight.departure')}
            value={fmtDateTime(group.assignedFlight?.departureAt ?? group.departureAt)}
          />
          <Field label={t('sheet.flight.cabin')} value={group.assignedFlight?.cabin ?? 'ECONOMY'} />
          <Field
            label={t('sheet.flight.seat')}
            value={group.assignedFlight?.seat ?? t('sheet.flight.seatAtCheckIn')}
          />
          <Field
            label={t('sheet.flight.ticket')}
            value={group.ticketNumber ?? t('sheet.flight.ticketPending')}
            mono
          />
        </div>
      </div>
      <p className="text-xs text-neutral-600">{t('sheet.flight.note')}</p>
    </section>
  );
}

function HotelSection({
  group,
  reservation,
}: {
  group: DisruptionPnrGroupRow;
  reservation: Reservation;
}) {
  const { t } = useTranslation('pnrActions');
  return (
    <section className="mt-6 break-inside-avoid space-y-2 text-sm" data-testid="sheet-hotel">
      <h2 className="text-xs font-semibold uppercase tracking-wide text-neutral-500">
        {t('sheet.hotel.title')}
      </h2>
      <div className="rounded-md border border-neutral-300 p-4">
        <p className="text-xl font-bold">
          {reservation.hotelName ?? t('sheet.hotel.assignedHotel')}
        </p>
        <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2">
          <Field label={t('sheet.hotel.rooms')} value={String(reservation.rooms ?? 1)} />
          <Field
            label={t('sheet.hotel.guests')}
            value={String(reservation.guests ?? group.paxCount)}
          />
          <Field
            label={t('sheet.hotel.coveredBy')}
            value={t('sheet.hotel.coveredByValue', { tenant: TENANT })}
          />
          <Field
            label={t('sheet.hotel.reference')}
            value={reservation.reservationUrn.split(':').pop() ?? ''}
            mono
          />
        </div>
      </div>
      <p className="text-xs text-neutral-600">{t('sheet.hotel.note')}</p>
    </section>
  );
}

/** The money, as the passenger has to use it — see `careMoney` for the rules. */
function CareCardSection({
  card,
  entitlement,
  balancesKnown,
  careUnavailable,
}: {
  card: CareCardRow | undefined;
  entitlement: DisruptionPnrGroupRow['mealVoucher'];
  balancesKnown: boolean;
  careUnavailable: boolean;
}) {
  const { t } = useTranslation('pnrActions');
  const { loadedLabel, availableLabel, cardLabel } = careMoney(
    card,
    entitlement,
    balancesKnown,
    t,
  );

  return (
    <section className="mt-6 break-inside-avoid space-y-2 text-sm" data-testid="sheet-care-card">
      <h2 className="text-xs font-semibold uppercase tracking-wide text-neutral-500">
        {t('sheet.care.title')}
      </h2>
      <div className="rounded-md border border-neutral-300 p-4">
        <p className="text-xl font-bold">
          {loadedLabel}{' '}
          <span className="font-normal">
            {cardLabel ? t('sheet.care.onCard', { card: cardLabel }) : t('sheet.care.assigned')}
          </span>
        </p>
        <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2">
          <Field label={t('sheet.care.loaded')} value={loadedLabel} />
          {availableLabel ? (
            <Field label={t('sheet.care.available')} value={availableLabel} />
          ) : null}
          {card?.last4 ? <Field label={t('sheet.care.card')} value={cardLabel ?? ''} mono /> : null}
          {card?.issuedAt ? (
            <Field label={t('sheet.care.issued')} value={fmtDateTime(card.issuedAt)} />
          ) : null}
        </div>
        {card?.pendingTopUp ? (
          <p className="mt-3 text-xs text-neutral-600">{t('sheet.care.pendingTopUp')}</p>
        ) : null}
      </div>
      {card ? (
        <p className="text-xs text-neutral-600">{t('sheet.care.note')}</p>
      ) : (
        <p className="text-xs text-neutral-600">
          {careUnavailable ? t('sheet.care.unreadable') : t('sheet.care.notHandedOver')}
        </p>
      )}
    </section>
  );
}

/**
 * Ground transport, including the case the single sheets could never state: the
 * ride EXISTS but has no time yet, or does not exist at all.
 *
 * Three live sources, in order of authority: the transport domain's own trips
 * (richest — provider, driver, plate, scheduled time), the roster's denormalised
 * `transfer` (a shuttle run + pickup time, which is what mass transfers look
 * like), and then nothing — which is itself the honest answer, printed as
 * "pending scheduling" rather than left blank.
 */
function TransportSection({
  group,
  trips,
  hasHotel,
  unavailable,
}: {
  group: DisruptionPnrGroupRow;
  trips: readonly TripView[];
  hasHotel: boolean;
  unavailable: boolean;
}) {
  const { t } = useTranslation('pnrActions');
  const station = group.overnightStation ?? group.destination;
  const transfer = group.transfer;
  const ownMeans = group.transportOutcome?.outcome === 'OWN_MEANS';

  return (
    <section className="mt-6 break-inside-avoid space-y-2 text-sm" data-testid="sheet-transport">
      <h2 className="text-xs font-semibold uppercase tracking-wide text-neutral-500">
        {t('sheet.transport.title')}
      </h2>
      <div className="rounded-md border border-neutral-300 p-4">
        {ownMeans ? (
          <p data-testid="transport-own-means">
            {t('sheet.transport.ownMeans')}
            {group.transportOutcome?.note ? ` ${group.transportOutcome.note}` : ''}
          </p>
        ) : trips.length ? (
          <ul className="space-y-3">
            {trips.map((trip) => (
              <li key={trip.urn} data-testid="transport-trip">
                <p className="font-semibold">{t(TRIP_LEG_LABEL_KEY[trip.leg])}</p>
                <p>{tripWhen(trip, station, t)}</p>
                <p className="text-xs text-neutral-600">
                  {[
                    trip.driverInfo?.name
                      ? t('sheet.transport.driverName', { name: trip.driverInfo.name })
                      : undefined,
                    trip.driverInfo?.vehicle,
                    trip.driverInfo?.licensePlate
                      ? t('sheet.transport.plate', { plate: trip.driverInfo.licensePlate })
                      : undefined,
                  ]
                    .filter(Boolean)
                    .join(' · ') || t('sheet.transport.vehiclePending')}
                </p>
              </li>
            ))}
          </ul>
        ) : transfer?.pickupTime ? (
          <p data-testid="transport-transfer">
            {transfer.kind === 'SHUTTLE'
              ? transfer.runSeq
                ? t('sheet.transport.shuttleRunFrom', { seq: transfer.runSeq, station })
                : t('sheet.transport.shuttleFrom', { station })
              : t('sheet.transport.transferFrom', { station })}{' '}
            {t('sheet.transport.at')}{' '}
            <span className="font-semibold">{formatFlightDateTime(transfer.pickupTime, station)}</span>{' '}
            ({t(TRIP_LEG_LABEL_KEY[transfer.leg])}). {t('sheet.transport.meetingPoint')}
          </p>
        ) : (
          <p data-testid="transport-pending">
            <span className="font-semibold">{t('sheet.transport.pending')}</span>{' '}
            {unavailable
              ? t('sheet.transport.pendingUnavailable')
              : hasHotel
                ? t('sheet.transport.pendingWithHotel')
                : t('sheet.transport.pendingNoHotel')}
          </p>
        )}
      </div>
    </section>
  );
}

function DirectionsSection({
  group,
  reservation,
  mapsHref,
}: {
  group: DisruptionPnrGroupRow;
  reservation: Reservation;
  mapsHref: string | undefined;
}) {
  const { t } = useTranslation('pnrActions');
  return (
    <section className="mt-6 break-inside-avoid space-y-3 text-sm" data-testid="sheet-directions">
      <h2 className="text-xs font-semibold uppercase tracking-wide text-neutral-500">
        {t('sheet.directions.title')}
      </h2>
      <div className="rounded-md border border-neutral-300 p-4">
        <p className="text-xl font-bold">
          {reservation.hotelName ?? t('sheet.hotel.assignedHotel')}
        </p>
        <ol className="mt-3 list-decimal space-y-2 pl-5">
          <li>{t('sheet.directions.shuttleStep')}</li>
          <li>
            {t('sheet.directions.taxiPrefix')}{' '}
            <span className="font-semibold">{reservation.hotelName}</span>{' '}
            {t('sheet.directions.taxiSuffix', { airport: group.destination })}
          </li>
          <li>
            {t('sheet.directions.mapStep')}{' '}
            {mapsHref ? (
              <a href={mapsHref} className="break-all underline">
                {mapsHref}
              </a>
            ) : (
              t('sheet.directions.mapFallback')
            )}
          </li>
        </ol>
      </div>
      <p className="text-xs text-neutral-600">{t('sheet.directions.note', { pnr: group.pnrLabel })}</p>
    </section>
  );
}

function Field({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <p>
      <span className="block text-[10px] uppercase tracking-wide text-neutral-500">{label}</span>
      <span className={mono ? 'font-mono font-semibold' : 'font-semibold'}>{value}</span>
    </p>
  );
}
